import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, Loader2, X, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface EnhancedFeedbackDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: string) => Promise<void>;
  type: 'approve' | 'reject';
  taskTitle?: string;        // Single task
  taskTitles?: string[];     // Multiple tasks
  taskCount?: number;        // Count for messaging
  isProcessing?: boolean;    // Processing state for loading UI
}

export const EnhancedFeedbackDropdown: React.FC<EnhancedFeedbackDropdownProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  type, 
  taskTitle,
  taskTitles,
  taskCount = 1,
  isProcessing = false
}) => {
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dynamic messaging based on single vs bulk
  const getPlaceholderText = () => {
    if (taskCount === 1) {
      return type === 'approve' 
        ? "Tell us why this task is important for achieving your goal..." 
        : "Tell us why this task wasn't relevant or helpful for your goal...";
    } else {
      return type === 'approve'
        ? "Tell us why these tasks are important for achieving your goal..."
        : "Tell us why these tasks weren't relevant or helpful for your goal...";
    }
  };

  const getHeaderText = () => {
    if (taskCount === 1) {
      return type === 'reject' ? "Why wasn't this relevant?" : "Why is this important?";
    } else {
      return type === 'reject' ? "Why weren't these relevant?" : "Why are these important?";
    }
  };

  const getDescriptionText = () => {
    return "Help us improve our suggestions";
  };

  const getTaskDisplayText = () => {
    if (taskCount === 1 && taskTitle) {
      return `"${taskTitle}"`;
    } else if (taskTitles && taskTitles.length > 0) {
      if (taskTitles.length <= 3) {
        return `"${taskTitles.join('", "')}"`;
      } else {
        return `"${taskTitles.slice(0, 2).join('", "')}" and ${taskTitles.length - 2} other task${taskTitles.length - 2 > 1 ? 's' : ''}`;
      }
    } else {
      return `${taskCount} task${taskCount > 1 ? 's' : ''}`;
    }
  };

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      toast.error('Please provide feedback before submitting');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit(feedback.trim());
      setFeedback('');
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setFeedback('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {/* Fixed backdrop overlay */}
      <motion.div
        key="feedback-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999]"
        onClick={handleClose}
      />
      
      {/* Centered modal container */}
      <motion.div
        key="feedback-modal"
        initial={{ opacity: 0, y: -20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.9 }}
        transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full max-w-md mx-auto">
        {/* Glass morphism container */}
        <div className="relative overflow-hidden rounded-2xl backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border border-white/20 dark:border-gray-700/30 shadow-2xl shadow-black/10 dark:shadow-black/30">
          {/* Gradient background overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-gray-800/60 dark:via-gray-800/30 dark:to-gray-900/10" />
          
          {/* Top border highlight */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-300/40 to-transparent dark:via-amber-500/30" />
          
          {/* Content */}
          <div className="relative p-6 space-y-5">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${
                  type === 'approve' 
                    ? 'bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40' 
                    : 'bg-gradient-to-br from-red-100 to-rose-100 dark:from-red-900/40 dark:to-rose-900/40'
                }`}>
                  {type === 'approve' ? (
                    <Plus className={`h-5 w-5 text-green-600 dark:text-green-400`} />
                  ) : (
                    <Minus className={`h-5 w-5 text-red-600 dark:text-red-400`} />
                  )}
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {getHeaderText()}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {getDescriptionText()}
                  </p>
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="h-8 w-8 p-0 rounded-full hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-colors"
              >
                <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </Button>
            </div>


            
            {/* Feedback textarea */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Your feedback
              </label>
              <div className="relative">
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder={getPlaceholderText()}
                  className="w-full h-24 p-4 text-sm rounded-xl border-0 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none focus:ring-2 focus:ring-amber-500/50 focus:outline-none transition-all duration-200 shadow-inner"
                  autoFocus
                />
                {/* Focus border */}
                <div className="absolute inset-0 rounded-xl border border-gray-200/50 dark:border-gray-700/50 pointer-events-none" />
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex items-center justify-end gap-3 pt-2">
                              <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClose}
                  disabled={isSubmitting || isProcessing}
                  className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-800/60 hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-all duration-200"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSubmit}
                  disabled={isSubmitting || isProcessing || !feedback.trim()}
                  className="px-6 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-medium shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                                  {isSubmitting || isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {isProcessing ? 'Sending to AI...' : 'Processing...'}
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-4 w-4 mr-2" />
                      Submit Feedback
                    </>
                  )}
              </Button>
            </div>
          </div>
        </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}; 